import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  loginPoolsToLoginPoolRowsAdapter,
  loginPoolFrontendAdapter,
  loginPoolBackendAdapter,
  loginPoolUpdateBackendAdapter,
} from '_/adapters/loginPools/loginPools.adapter';
import { requestLoginPools, requestLoginPool, postLoginPool, patchLoginPool } from '_/api/projects/projectLoginPools.api';
import { IUILoginPoolValues } from '_/types/loginPools';
import { notification } from 'antd';

// Query keys
export const LOGIN_POOLS_QUERY_KEY = (projectId: string, orgSlug?: string, projectSlug?: string): (string | undefined)[] => [
  'loginPools',
  projectId,
  orgSlug,
  projectSlug,
];
export const LOGIN_POOL_QUERY_KEY = (projectId: string, slug: string): string[] => ['project', projectId, 'loginPool', slug];

/**
 * Hook to fetch all login pools for a project
 */
export const useLoginPools = (projectId?: string, orgSlug?: string, projectSlug?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: LOGIN_POOLS_QUERY_KEY(projectId || '', orgSlug, projectSlug),
    queryFn: async () => {
      const response = await requestLoginPools(projectId!);
      // Apply adapter to add hrefs and format dates
      return loginPoolsToLoginPoolRowsAdapter(response.items, orgSlug!, projectSlug!);
    },
    enabled: !!(projectId && orgSlug && projectSlug),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a single login pool
 */
export const useLoginPool = (projectId?: string, slug?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: LOGIN_POOL_QUERY_KEY(projectId || '', slug || ''),
    queryFn: async () => {
      const loginPool = await requestLoginPool(projectId!, slug!);
      // Apply frontend adapter to convert to UI format
      return loginPoolFrontendAdapter(loginPool);
    },
    enabled: !!(projectId && slug),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to create a login pool
 */
export const useCreateLoginPool = (
  projectId?: string,
  onSuccessCallback?: () => void,
): UseMutationResult<any, Error, IUILoginPoolValues> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (loginPoolValues: IUILoginPoolValues) => {
      // Apply backend adapter to convert from UI format to API format
      const loginPool = loginPoolBackendAdapter(loginPoolValues);
      return postLoginPool(projectId!, loginPool);
    },
    onSuccess: () => {
      // Invalidate login pools list
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: LOGIN_POOLS_QUERY_KEY(projectId) });
      }
      notification.success({
        message: 'Login Pool Created',
      });
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      console.error('Error creating login pool', error);
      notification.error({
        message: error.messages?.[0] || 'Error creating login pool',
      });
    },
  });
};

/**
 * Hook to update a login pool
 */
export const useUpdateLoginPool = (
  projectId?: string,
  loginPoolId?: string,
  slug?: string,
  onSuccessCallback?: () => void,
): UseMutationResult<any, Error, IUILoginPoolValues> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (loginPoolValues: IUILoginPoolValues) => {
      // Apply update backend adapter to convert from UI format to API format
      // Update only sends name and fields (no slug or parameters)
      const loginPool = loginPoolUpdateBackendAdapter(loginPoolValues);
      return patchLoginPool(loginPoolId!, loginPool);
    },
    onSuccess: () => {
      // Invalidate the specific login pool query
      if (projectId && slug) {
        void queryClient.invalidateQueries({ queryKey: LOGIN_POOL_QUERY_KEY(projectId, slug) });
      }
      // Invalidate login pools list
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: LOGIN_POOLS_QUERY_KEY(projectId) });
      }
      notification.success({
        message: 'Login Pool Updated',
      });
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      console.error('Error updating login pool', error);
      notification.error({
        message: error.messages?.[0] || 'Error updating login pool',
      });
    },
  });
};
